import React, { useEffect, useState } from 'react'
import { Typography, Select, Card, Button, Modal, Form, Input, DatePicker, Table, message, Popconfirm, Empty, Spin } from 'antd'
import { PlusOutlined, DeleteOutlined, LineChartOutlined } from '@ant-design/icons'
import { progressAPI, clientsAPI } from '../services/api'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import dayjs from 'dayjs'

const { Title, Text } = Typography

function Progreso() {
  const [clients, setClients] = useState([])
  const [clientId, setClientId] = useState(null)
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [form] = Form.useForm()

  useEffect(() => {
    clientsAPI.getAll().then(res => setClients(res.data)).catch(() => {})
  }, [])

  const loadEntries = async (id) => {
    setLoading(true)
    try {
      const res = await progressAPI.getByClient(id)
      setEntries(res.data)
    } catch (e) { message.error('Error cargando progreso') }
    setLoading(false)
  }

  const handleClientChange = (id) => {
    setClientId(id)
    if (id) loadEntries(id)
    else setEntries([])
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      await progressAPI.create({
        clientId,
        date: values.date ? values.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        weight: values.weight,
        measurement: values.measurement,
        notes: values.notes
      })
      message.success('Registro guardado')
      setModalVisible(false)
      form.resetFields()
      loadEntries(clientId)
    } catch (e) { message.error('Error guardando') }
  }

  const handleDelete = async (id) => {
    try {
      await progressAPI.remove(id)
      message.success('Registro eliminado')
      loadEntries(clientId)
    } catch (e) { message.error('Error') }
  }

  const chartData = entries.map(e => ({
    date: dayjs(e.date).format('DD/MM'),
    peso: e.weight
  })).filter(e => e.peso != null)

  const columns = [
    { title: 'Fecha', dataIndex: 'date', render: (d) => dayjs(d).format('DD/MM/YY'), width: 80 },
    { title: 'Peso (kg)', dataIndex: 'weight', render: (v) => v != null ? `${v} kg` : '—', width: 100 },
    { title: 'Medición', dataIndex: 'measurement', render: (v) => v || '—' },
    { title: 'Notas', dataIndex: 'notes', ellipsis: true, render: (v) => v || '—' },
    { title: '', key: 'actions', width: 50, render: (_, r) => (
      <Popconfirm title="¿Eliminar registro?" onConfirm={() => handleDelete(r.id)}>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    )}
  ]

  return (
    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Title level={4} style={{ margin: 0 }}>Progreso</Title>
        <Button type="primary" icon={<PlusOutlined />} disabled={!clientId} onClick={() => { form.resetFields(); setModalVisible(true) }}>
          Nuevo Registro
        </Button>
      </div>

      <Card size="small" style={{ marginBottom: 12, borderRadius: 12 }}>
        <Text style={{ display: 'block', marginBottom: 8, color: '#999' }}>Elegí una alumna para ver su evolución:</Text>
        <Select
          showSearch
          value={clientId}
          onChange={handleClientChange}
          placeholder="Buscar alumna..."
          style={{ width: '100%', maxWidth: 400 }}
          optionFilterProp="children"
          filterOption={(input, option) => (option.children || '').toString().toLowerCase().includes(input.toLowerCase())}
        >
          {clients.map(c => <Select.Option key={c.id} value={c.id}>{c.name} {c.lastName}</Select.Option>)}
        </Select>
      </Card>

      {clientId && (
        <>
          {chartData.length > 1 && (
            <Card size="small" style={{ marginBottom: 12, borderRadius: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}><LineChartOutlined /> Evolución de peso</Text>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#888" fontSize={11} />
                  <YAxis stroke="#888" fontSize={11} domain={['dataMin - 1', 'dataMax + 1']} />
                  <Tooltip contentStyle={{ background: '#222', border: '1px solid #444', borderRadius: 8 }} />
                  <Line type="monotone" dataKey="peso" stroke="#ffffff" strokeWidth={2} dot={{ fill: '#ffffff', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          <div style={{ overflowX: 'auto' }}>
            <Table
              dataSource={entries}
              columns={columns}
              rowKey="id"
              size="small"
              scroll={{ x: 480 }}
              pagination={{ pageSize: 10, size: 'small' }}
              loading={loading}
              locale={{ emptyText: <Empty description="Sin registros todavía" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </div>
        </>
      )}

      {!clientId && (
        <Card style={{ borderRadius: 12, textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
          <Text style={{ display: 'block', marginTop: 12, color: '#999' }}>Seleccioná una alumna para empezar</Text>
        </Card>
      )}

      <Modal title="Nuevo Registro" open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} okText="Guardar" width={420}>
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="date" label="Fecha" initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="weight" label="Peso (kg)">
            <Input type="number" step="0.1" placeholder="Ej: 62.5" />
          </Form.Item>
          <Form.Item name="measurement" label="Medición">
            <Input placeholder="Ej: Cintura 72cm" />
          </Form.Item>
          <Form.Item name="notes" label="Notas">
            <Input.TextArea rows={2} placeholder="Sensación, rendimiento..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Progreso
